import { useState, type ReactNode } from 'react'
import { Link, Stack, Tooltip, Typography } from '@mui/material'
import { RadioIcon, RefreshCwIcon } from 'lucide-react'

import { Button } from '@repo/ui'

import type { ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useRescanAllCompetitorFeeds, useRescanCompetitorFeeds } from 'hooks/api'
import { getErrorMessage } from 'utils'

import {
  MarketSourceFormModal,
  type MarketSourcePrefill,
} from '../InternalMarketSourcesPage/MarketSourceFormModal'

/**
 * VRG-741 — a competitor's FEEDS, as one small reusable piece so it moves with the competitor
 * profile (Spencer plans a top-level "Competitor research" page; today it sits on GTM → SEO →
 * Competitors).
 *
 * Feeds are found and added AUTOMATICALLY (when a competitor is approved, then on the market
 * poller's tick; "no feed found" is retried weekly). This piece shows the result and offers the two
 * manual actions: "Re-scan feeds" (look again now) and "Add feed" (register one by hand, through the
 * shared source form). It needs only the competitor's domain, name, feed count and last check.
 */

export interface CompetitorFeedsSubject {
  domain: string
  name: string | null
  feedCount: number
  feedCheck: { checkedAt: string | null; outcome: 'found' | 'none' | 'error'; note: string | null } | null
}

const MANAGE_ONLY = 'Feeds need the market-data manage permission.'

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : ''

/** The Feeds cell: the count, or "No feed found · 23 Oct 26", or "Not checked yet". */
export const CompetitorFeedsCell = ({ competitor: c }: { competitor: CompetitorFeedsSubject }) => {
  if (c.feedCount > 0) return <>{c.feedCount}</>
  const check = c.feedCheck
  const text = !check
    ? 'Not checked yet'
    : check.outcome === 'error'
      ? `Check failed · ${day(check.checkedAt)}`
      : `No feed found · ${day(check.checkedAt)}`
  const body = (
    <Typography variant="body4" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {text}
    </Typography>
  )
  return check?.note ? (
    <Tooltip title={check.note} placement="top">
      <span>{body}</span>
    </Tooltip>
  ) : (
    body
  )
}

/**
 * The two actions, as row-menu items plus the form they open. Render `modal` once, next to the
 * table; spread `menuItems(c)` into the competitor's row menu.
 */
export const useCompetitorFeedActions = (): {
  menuItems: (c: CompetitorFeedsSubject) => ActionDropdownItem[]
  addFeedLink: (c: CompetitorFeedsSubject) => ReactNode
  modal: ReactNode
} => {
  const { open: notify } = useSnackbar()
  const { isHasAccess: canManage } = useCanAccess({ action: 'internal_market_intel_manage' })
  const rescan = useRescanCompetitorFeeds()
  const [feedFor, setFeedFor] = useState<MarketSourcePrefill | null>(null)

  const addFeed = (c: CompetitorFeedsSubject) =>
    setFeedFor({ competitorDomain: c.domain, name: `${c.name?.trim() || c.domain} blog` })

  const runRescan = (c: CompetitorFeedsSubject) =>
    rescan.mutate(c.domain, {
      onSuccess: (r) =>
        notify({
          type: r.outcome === 'error' ? 'error' : 'success',
          text: r.added.length
            ? `${c.name || c.domain}: added ${r.added.length} feed${r.added.length === 1 ? '' : 's'}. ${r.note}`
            : `${c.name || c.domain}: ${r.note}`,
        }),
      onError: (e) => notify({ type: 'error', text: getErrorMessage(e) || 'Could not scan for feeds.' }),
    })

  return {
    menuItems: (c) => [
      {
        label: 'Re-scan feeds',
        icon: <RefreshCwIcon size={18} strokeWidth={1} />,
        disabled: !canManage || rescan.isPending,
        tooltip: !canManage ? MANAGE_ONLY : undefined,
        onClick: () => runRescan(c),
      },
      {
        label: 'Add feed',
        icon: <RadioIcon size={18} strokeWidth={1} />,
        disabled: !canManage,
        tooltip: !canManage ? MANAGE_ONLY : undefined,
        onClick: () => addFeed(c),
      },
    ],
    addFeedLink: (c) =>
      canManage && c.feedCount === 0 ? (
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="baseline">
          <CompetitorFeedsCell competitor={c} />
          <Link component="button" type="button" underline="hover" onClick={() => addFeed(c)} sx={{ font: 'inherit' }}>
            Add
          </Link>
        </Stack>
      ) : (
        <CompetitorFeedsCell competitor={c} />
      ),
    modal: (
      <MarketSourceFormModal
        open={Boolean(feedFor)}
        prefill={feedFor}
        onClose={() => setFeedFor(null)}
        onSaved={(row) => notify({ type: 'success', text: `Added ${row.name}. It is polled with every other source.` })}
      />
    ),
  }
}

/**
 * "Re-scan feeds" for every due competitor (never checked, or no feed found a week or more ago),
 * in the background. For a tab or page header, beside its primary action.
 */
export const RescanAllFeedsButton = () => {
  const { open: notify } = useSnackbar()
  const { isHasAccess: canManage } = useCanAccess({ action: 'internal_market_intel_manage' })
  const rescan = useRescanAllCompetitorFeeds()
  return (
    <Button
      variant="secondary"
      startIcon={<RefreshCwIcon strokeWidth={1} size={18} />}
      disabled={!canManage || rescan.isPending}
      disabledReason={!canManage ? MANAGE_ONLY : undefined}
      loading={rescan.isPending}
      onClick={() =>
        rescan.mutate(undefined, {
          onSuccess: (r) =>
            notify({
              type: 'success',
              text: r.alreadyRunning
                ? 'A feed scan is already running.'
                : r.queued
                  ? `Scanning ${r.queued} competitor${r.queued === 1 ? '' : 's'} for blog feeds. New feeds appear here as they are found.`
                  : 'Every competitor was checked in the last week. Re-scan one from its row on GTM → SEO → Competitors.',
            }),
          onError: (e) => notify({ type: 'error', text: getErrorMessage(e) || 'Could not start the feed scan.' }),
        })
      }
    >
      Re-scan feeds
    </Button>
  )
}
